import { useCallback, useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { numTrabajo } from "@/pages/TrabajosActivos";
import { Gauge, TriangleAlert } from "lucide-react";

const fmtNum = (n) => Number(n || 0).toLocaleString("es-CR");

const fmtFechaHora = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString("es-CR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function UsoIA() {
  const [dias, setDias] = useState("30");
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/ia-uso/resumen", { params: { dias } });
      setResumen(data);
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setCargando(false); }
  }, [dias]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div data-testid="uso-ia-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <Gauge size={20} className="text-indigo-600" />
          <h1 className="text-lg font-bold text-slate-800">Uso de IA</h1>
        </div>
        <Select value={dias} onValueChange={setDias}>
          <SelectTrigger data-testid="uso-ia-dias-select" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 días</SelectItem>
            <SelectItem value="30">Últimos 30 días</SelectItem>
            <SelectItem value="90">Últimos 90 días</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-sm text-slate-500 -mt-2">
        Consumo de tokens de Anthropic por las funciones de IA del sistema (chat, sugerencias de repuestos, FRT,
        análisis de detalle, equivalentes). Las consultas con más de 300 000 tokens quedan marcadas como alerta.
      </p>

      {cargando ? (
        <p className="text-sm text-slate-500">Cargando...</p>
      ) : !resumen ? null : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Panel titulo="Total de tokens" testId="uso-ia-total-tokens-panel">
              <p data-testid="uso-ia-total-tokens" className="text-3xl font-bold text-slate-800">
                {fmtNum(resumen.total_tokens_periodo)}
              </p>
              <p className="text-xs text-slate-500 mt-1">en {resumen.total_consultas_periodo} consulta(s) en los últimos {resumen.dias} días</p>
            </Panel>
            <Panel titulo="Totales por función" testId="uso-ia-por-funcion-panel">
              {resumen.por_funcion.length === 0 ? (
                <p className="text-sm text-slate-400">Sin datos en el período.</p>
              ) : (
                <div className="space-y-1.5">
                  {resumen.por_funcion.map((f) => (
                    <div key={f.funcion} data-testid={`uso-ia-funcion-${f.funcion}`}
                      className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{f.funcion}</span>
                      <span className="text-slate-500">{fmtNum(f.total_tokens)} tokens · {f.consultas} consulta(s)</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>

          <Panel titulo="Totales por día" testId="uso-ia-por-dia-panel">
            {resumen.por_dia.length === 0 ? (
              <p data-testid="uso-ia-por-dia-vacio" className="text-sm text-slate-400">Sin datos en el período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table data-testid="uso-ia-por-dia-tabla" className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                      <th className="py-2 pr-3 font-semibold">Día</th>
                      <th className="py-2 pr-3 font-semibold">Consultas</th>
                      <th className="py-2 font-semibold">Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...resumen.por_dia].reverse().map((d) => (
                      <tr key={d.dia} data-testid={`uso-ia-dia-${d.dia}`} className="border-b border-slate-100">
                        <td className="py-2 pr-3 text-slate-700">{d.dia}</td>
                        <td className="py-2 pr-3 text-slate-600">{d.consultas}</td>
                        <td className="py-2 text-slate-800 font-medium">{fmtNum(d.total_tokens)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel titulo="Consultas más grandes" testId="uso-ia-top-panel">
            {resumen.top_consultas.length === 0 ? (
              <p data-testid="uso-ia-top-vacio" className="text-sm text-slate-400">Sin datos en el período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table data-testid="uso-ia-top-tabla" className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                      <th className="py-2 pr-3 font-semibold">Función</th>
                      <th className="py-2 pr-3 font-semibold">Usuario</th>
                      <th className="py-2 pr-3 font-semibold">Trabajo</th>
                      <th className="py-2 pr-3 font-semibold">Vueltas</th>
                      <th className="py-2 pr-3 font-semibold">Tokens</th>
                      <th className="py-2 font-semibold">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumen.top_consultas.map((c) => (
                      <tr key={c.id} data-testid={`uso-ia-top-fila-${c.id}`}
                        className={`border-b border-slate-100 ${c.alerta ? "bg-red-50" : ""}`}>
                        <td className="py-2 pr-3 text-slate-700">{c.funcion}</td>
                        <td className="py-2 pr-3 text-slate-600">{c.usuario || "—"}</td>
                        <td className="py-2 pr-3 text-indigo-700 font-medium">
                          {c.trabajo_numero ? numTrabajo(c.trabajo_numero) : "—"}
                        </td>
                        <td className="py-2 pr-3 text-slate-600">{c.vueltas}</td>
                        <td className="py-2 pr-3 font-medium">
                          <span className={`inline-flex items-center gap-1 ${c.alerta ? "text-red-700" : "text-slate-800"}`}>
                            {c.alerta && <TriangleAlert size={13} data-testid={`uso-ia-alerta-${c.id}`} />}
                            {fmtNum(c.total_tokens)}
                          </span>
                        </td>
                        <td className="py-2 text-slate-500 text-xs">{fmtFechaHora(c.creado_en)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
